<ph-dialog [open]="open" size="xl" heading="Print a check" (ph-close)="close()">
    <ph-stepper [items]="stepLabels" [current]="step"></ph-stepper>

    <div *ngIf="currentStepLabel === 'Details'" [formGroup]="checkForm">
        <div formArrayName="checks" class="check-form-container">
            <ph-label>Add check details</ph-label>
            <ph-hint>
                We support the Quickbooks Voucher and Standard (3-per-page) formats.<br>You can order check stock from
                Quickbooks here:
            </ph-hint>
            <ul>
                <li>
                    <a href="https://intuitmarket.intuit.com/checks/voucher-checks" target="_blank" rel="noopener">Voucher
                        Checks</a>
                </li>
                <li>
                    <a href="https://intuitmarket.intuit.com/checks/standard-checks" target="_blank" rel="noopener">Standard Checks
                        (3-per-page)</a>
                </li>
            </ul>
            <div *ngFor="let check of checks.controls; let i = index; trackBy: trackByIndex" [formGroupName]="i" [@slide]="'*'">
                <ph-card [id]="'checkFormElement_' + i">
                    <div *ngIf="i === currentCheckIndex; else condensedCheck">
                        <!-- Full form for the current check -->
                        <ph-toolbar justify="between">
                            <ph-label>Check #{{ i + 1 }}</ph-label>
                            <ph-compact-button *ngIf="checks.length > 1" variant="ghost" icon="trash" aria-label="Delete check" (click)="deleteCheck(i)"></ph-compact-button>
                        </ph-toolbar>
                        <ph-stack>
                            <ph-field>
                                <ph-label>Payee type</ph-label>
                                <ph-radio-group formControlName="payeeType" ngDefaultControl [items]="payeeTypeItems" (ph-change)="handlePayeeTypeChange($event.detail)"></ph-radio-group>
                            </ph-field>
                            <ph-field>
                                <div *ngIf="currentCheck.get('payeeType').value === 'other'; else vendorForm">
                                    <ph-input formControlName="payee" ngDefaultControl placeholder="Payee name" aria-label="Payee name"></ph-input>
                                </div>
                                <ng-template #vendorForm>
                                    <div>
                                        <ph-select formControlName="vendorId" ngDefaultControl placeholder="Select or create a vendor" aria-label="Select or create a vendor" [items]="vendorItems" (ph-change)="checkAddVendor($event.detail)"></ph-select>
                                    </div>
                                </ng-template>
                            </ph-field>
                            <ph-field>
                                <ph-label>Bank account</ph-label>
                                <app-lob-bank-account-selector
                                    [formGroup]="currentCheck"
                                    [lobBankAccounts]="verifiedBankAccounts"
                                    controlName="lobBankAccountId"
                                ></app-lob-bank-account-selector>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Amount</ph-label>
                                <ph-money-input formControlName="amount"
                                                ngDefaultControl
                                                [hasError]="currentCheck.get('amount').touched && currentCheck.get('amount').invalid"></ph-money-input>
                            </ph-field>
                            <ph-field>
                                <ph-label>Payment date</ph-label>
                                <ph-date-input formControlName="checkDate" ngDefaultControl placeholder="Check date"></ph-date-input>
                            </ph-field>
                            <ph-field>
                                <app-check-number-input
                                    [formGroup]="currentCheck"
                                    controlName="checkNumber"
                                    bankAccountControlName="lobBankAccountId"
                                    [bankAccounts]="verifiedBankAccounts"
                                    [checkNumberOverride]="i > 0 ? (checks.at(i - 1).get('checkNumber').value + 1) : null"
                                ></app-check-number-input>
                            </ph-field>
                            <ph-field>
                                <ph-label>Memo</ph-label>
                                <ph-textarea formControlName="memo" ngDefaultControl placeholder="Optional" rows="2"></ph-textarea>
                            </ph-field>
                        </ph-stack>
                    </div>
                    <ng-template #condensedCheck>
                        <!-- Condensed version for other checks -->
                        <div>
                            <ph-toolbar justify="between">
                                <ph-label>{{ check.value.amount / 100 | currency:'USD':'symbol' }}</ph-label>
                                <ph-toolbar>
                                    <ph-compact-button *ngIf="checks.length > 1" variant="ghost" icon="trash" aria-label="Delete check" (click)="deleteCheck(i)"></ph-compact-button>
                                    <ph-button variant="secondary" mode="filled" size="xsmall" icon="pencil-simple" (click)="editCheck(i)">Edit</ph-button>
                                </ph-toolbar>
                            </ph-toolbar>
                            <div class="bold">{{ getVendorName(check) }}</div>
                            <ph-hint>{{ check.get('checkNumber').value }}
                                • {{ check.get('checkDate').value | legfiDate:'MM/DD/YYYY' }}
                            </ph-hint>
                        </div>
                    </ng-template>
                </ph-card>
            </div>
            <ph-link-button *ngIf="!payable" (click)="addCheck()">Add another check</ph-link-button>
            <ph-button (click)="advanceFromDetails()">Continue</ph-button>
        </div>
    </div>

    <div [hidden]="currentStepLabel !== 'Format'">
        <ph-page-header>
            <ph-button *ngIf="hasDetailsStep"
                       slot="back"
                       variant="secondary"
                       mode="filled"
                       icon-only
                       full-radius
                       icon="arrow-left"
                       aria-label="Back"
                       [disabled]="isLoading || !!checkBatch"
                       (click)="handleStepChange(step - 1)"></ph-button>
            Select format
        </ph-page-header>

        <app-check-format-options #formatOptions></app-check-format-options>

        <ph-button (click)="handleStepChange(step + 1)">Continue</ph-button>
    </div>

    <div *ngIf="currentStepLabel === 'Review'">
        <div *ngIf="isLoading; else checkPreviewContainer">
            <ph-skeleton lines="6" size="lg"></ph-skeleton>
        </div>
        <ng-template #checkPreviewContainer>
            <div>
                <ph-page-header>
                    <ph-button slot="back"
                               variant="secondary"
                               mode="filled"
                               icon-only
                               full-radius
                               icon="arrow-left"
                               aria-label="Back"
                               [disabled]="isLoading || !!checkBatch"
                               (click)="handleStepChange(step - 1)"></ph-button>
                    {{ checkBatch ? 'Download & print' : 'Review check information' }}
                </ph-page-header>
                <ph-stack>
                    <app-check-printing-preview *ngFor="let checkPreview of previewData; trackBy: trackByIndex" [check]="checkPreview" [displayVendorAddress]="formatOptions?.getFormatValues()?.displayVendorAddress"></app-check-printing-preview>
                </ph-stack>
                <ph-button *ngIf="checkBatch; else createCheckButton"
                           icon="download-simple"
                           [disabled]="isLoading"
                           (click)="downloadPrintedCheckFile()">Download check batch file</ph-button>
                <ng-template #createCheckButton>
                    <ph-button [disabled]="isLoading" (click)="submitForm()">{{ createButtonText }}</ph-button>
                </ng-template>
            </div>
        </ng-template>
    </div>
</ph-dialog>

<app-vendor-editor-dialog [open]="addVendorOpen" (closed)="onAddVendorClosed($event)"></app-vendor-editor-dialog>

<app-confirm-dialog [open]="duplicateConfirmOpen"
                    heading="Possible duplicate check"
                    message="A similar check has already been printed in your organization within the last 14 days:"
                    confirmLabel="Print anyway"
                    (closed)="onDuplicateConfirmClosed($event)">
    <ul>
        <li *ngFor="let line of duplicateMatches; trackBy: trackByIndex">{{ line }}</li>
    </ul>
    <p>Print this check anyway?</p>
</app-confirm-dialog>
